import {
  ShouldUpdateScroll,
  ScrollPosition,
} from '@respond-framework/scroll-restorer';
import $ from 'jquery';
import { LocationEntry } from '@respond-framework/types';
import { ROUTE_COURSE_PAGE, RouteAction } from './routeActions';
import { animateHashScrollKinds } from '../web/rudyMiddlewares/animateHashScrollKinds';
import {
  isModuleViewerChange,
  isSameRoute,
  isWithinCourseEnvironment,
} from './location/helpers';
import { routeIsInCourseWithQueryParam } from './location/CurrentCoursePath/constants';

/**
 * Find the index of the entry in the history stack (if it exists)
 * where the current page was being viewed (with the module viewer closed),
 * and any entries in between have the module viewer open.
 */
const historyIndexBeforeOpeningModuleViewer = (
  entries: LocationEntry<RouteAction>[],
  currentIndex: number,
  index: number
): number | null => {
  if (index < 0) {
    return null;
  }
  const entry = entries[index];
  if (isSameRoute(entry, entries[currentIndex])) {
    return index;
  }
  if (isModuleViewerChange(entry, entries[currentIndex])) {
    return historyIndexBeforeOpeningModuleViewer(
      entries,
      currentIndex,
      index - 1
    );
  }
  return null;
};

export const shouldUpdateScroll: ShouldUpdateScroll<RouteAction> = ({
  action,
  scrollRestorer,
}) => {
  const { hash } = action;
  if (!('location' in action)) {
    return false;
  }
  if (hash && animateHashScrollKinds.includes(action.location.kind)) {
    /**
     * In the case that the URL contains a hash, let the `scrollToHash` middleware
     * handle it this is because
     *  - rudy currently does not call middlewares after transformAction if only
     *    the hash has changed
     *  - the restore scroll middleware does not handle animated scrolling
     */
    return false;
  }
  const {
    location: { kind, entries, index, prev },
  } = action;
  if (
    action.type === ROUTE_COURSE_PAGE ||
    routeIsInCourseWithQueryParam(action)
  ) {
    // Find the position scrolled to before the transition
    let previousPositionInCourseEnvironment: ScrollPosition | null = null;
    if (
      prev &&
      'location' in prev &&
      prev.type !== '' && // i.e., there is no previous location
      scrollRestorer &&
      isWithinCourseEnvironment(action, prev)
    ) {
      previousPositionInCourseEnvironment = scrollRestorer.readScrollPosition(
        prev,
        null
      );
    }

    // Find where we want to scroll to next (except hashes, not covered here)
    let targetPosition: ScrollPosition = [0, 0];
    if (scrollRestorer) {
      // There might be a saved position when using the back/next buttons
      targetPosition =
        scrollRestorer.readScrollPosition(entries[index], null) ||
        targetPosition;
    }
    const indexBeforeModuleViewer =
      kind === 'push' &&
      index >= 2 &&
      !action.query.moduleSet &&
      isModuleViewerChange(action, entries[index - 1])
        ? historyIndexBeforeOpeningModuleViewer(entries, index, index - 2)
        : null;
    if (scrollRestorer && typeof indexBeforeModuleViewer === 'number') {
      // The user might be closing he module viewer (in this case it behaves
      // like a wave, not a particle)
      targetPosition =
        scrollRestorer.readScrollPosition(
          entries[indexBeforeModuleViewer],
          null
        ) || targetPosition;
    }

    /**
     * When transitioning between pages within the course environment,
     * the course header bar does not change. So avoid scrolling unecessarily
     * if the only effect is to scroll it in and out of view
     */
    if (previousPositionInCourseEnvironment) {
      const [targetX, targetY] = targetPosition;
      const prevY = previousPositionInCourseEnvironment[1];
      const ceiling = Math.min($('#site-page-header').height() || 0, prevY);
      return [targetX, Math.max(ceiling, targetY)];
    }

    return true;
  } else {
    /**
     * Use the default behaviour (restore existing position from history,
     * scroll to URL hash, or scroll to top)
     */
    return true;
  }
};
